import React, { useEffect, useState } from 'react'

import { supabase } from '../../lib/supabase'
import { toast } from 'sonner'
import { Card } from '../../components/UI'
import {
    ToggleRight, ToggleLeft, Search, RefreshCw, Copy, Shield,
    ChevronDown, Settings2, History, Code2
} from 'lucide-react'

// ============================================
// Flags predefinidos del sistema
// ============================================

const SYSTEM_FLAGS = [
    { key: 'maintenance_mode', label: 'Modo Mantenimiento', desc: 'Activa modo de mantenimiento en la app cliente', icon: '🔧', color: 'text-yellow-400' },
    { key: 'dark_mode', label: 'Dark Mode', desc: 'Fuerza tema oscuro en la app cliente', icon: '🌙', color: 'text-indigo-400' },
    { key: 'beta_features', label: 'Beta Features', desc: 'Habilita funcionalidades experimentales', icon: '🧪', color: 'text-purple-400' },
    { key: 'debug_mode', label: 'Debug Mode', desc: 'Activa logs verbose y herramientas de debug', icon: '🐛', color: 'text-green-400' },
    { key: 'analytics_enabled', label: 'Analytics', desc: 'Habilita recopilación de telemetría extendida', icon: '📊', color: 'text-blue-400' },
    { key: 'readonly_mode', label: 'Solo Lectura', desc: 'Desactiva operaciones de escritura', icon: '🔒', color: 'text-red-400' },
    { key: 'notifications', label: 'Notificaciones', desc: 'Habilita push notifications al usuario', icon: '🔔', color: 'text-cyan-400' },
    { key: 'auto_update', label: 'Auto Update', desc: 'Permite actualizaciones automáticas del agente', icon: '🔄', color: 'text-emerald-400' },
]

interface License {
    id: string
    client_name: string
    client_id: string
    features: Record<string, boolean> | null
    projects?: { name: string }
}

type TabType = 'flags' | 'history' | 'raw'

export default function FeatureFlagsPage() {
    const [licenses, setLicenses] = useState<License[]>([])
    const [selectedLicense, setSelectedLicense] = useState<string>('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [search, setSearch] = useState('')
    const [tab, setTab] = useState<TabType>('flags')
    const [changeLog, setChangeLog] = useState<Array<{ time: string; flag: string; value: boolean; license: string }>>([])

    useEffect(() => { fetchLicenses() }, [])

    const fetchLicenses = async () => {
        try {
            const { data, error } = await supabase
                .from('licenses')
                .select('id, client_name, client_id, features, projects(name)')
                .order('client_name')
            if (error) throw error
            setLicenses(data || [])
            if (data?.length && !selectedLicense) setSelectedLicense(data[0].id)
        } catch {
            toast.error('Error cargando licencias')
        } finally {
            setLoading(false)
        }
    }

    const currentLicense = licenses.find(l => l.id === selectedLicense)
    const currentFeatures: Record<string, boolean> = currentLicense?.features || {}

    const toggleFlag = async (flagKey: string) => {
        if (!currentLicense) return
        setSaving(true)
        const newValue = !currentFeatures[flagKey]
        const updatedFeatures = { ...currentFeatures, [flagKey]: newValue }

        try {
            const { error } = await supabase
                .from('licenses')
                .update({ features: updatedFeatures })
                .eq('id', currentLicense.id)
            if (error) throw error

            setLicenses(prev => prev.map(l =>
                l.id === currentLicense.id ? { ...l, features: updatedFeatures } : l
            ))
            setChangeLog(prev => [{
                time: new Date().toLocaleTimeString('es-ES'),
                flag: flagKey,
                value: newValue,
                license: currentLicense.client_name
            }, ...prev].slice(0, 50))
            toast.success(`${flagKey}: ${newValue ? 'ON' : 'OFF'}`)
        } catch {
            toast.error('Error actualizando flag')
        } finally {
            setSaving(false)
        }
    }

    const filteredFlags = SYSTEM_FLAGS.filter(f =>
        f.label.toLowerCase().includes(search.toLowerCase()) ||
        f.key.toLowerCase().includes(search.toLowerCase())
    )

    const tabs: Array<{ id: TabType; label: string; icon: React.ReactNode }> = [
        { id: 'flags', label: 'Feature Flags', icon: <ToggleRight size={16} /> },
        { id: 'history', label: 'Historial', icon: <History size={16} /> },
        { id: 'raw', label: 'JSON Raw', icon: <Code2 size={16} /> },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <ToggleRight className="text-neon-blue" /> Feature Flags
                </h1>
                <p className="text-gray-500 text-sm">Control remoto de funcionalidades por licencia</p>
            </div>

            {/* Selector de Licencia */}
            <Card className="mb-6">
                <div className="flex items-center gap-4">
                    <Shield size={18} className="text-neon-blue shrink-0" />
                    <div className="relative flex-1">
                        <select
                            value={selectedLicense}
                            onChange={e => setSelectedLicense(e.target.value)}
                            className="w-full bg-charcoal border border-white/10 rounded-lg px-4 py-3 text-white appearance-none focus:border-neon-blue focus:ring-1 focus:ring-neon-blue outline-none transition-all"
                        >
                            {licenses.map(l => (
                                <option key={l.id} value={l.id}>
                                    {l.client_name} — {l.projects?.name || 'Sin proyecto'}
                                </option>
                            ))}
                        </select>
                        <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                    <button onClick={fetchLicenses} className="p-2.5 rounded-lg bg-white/5 text-gray-400 hover:text-white hover:bg-white/10 transition-colors" title="Recargar">
                        <RefreshCw size={16} />
                    </button>
                </div>
            </Card>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'
                            }`}
                    >
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {/* Tab: Flags */}
            {tab === 'flags' && (
                <>
                    <div className="relative mb-4">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <input
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Buscar flag..."
                            className="w-full bg-charcoal border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-neon-blue transition-all"
                        />
                    </div>

                    <div className="grid gap-3">
                        {filteredFlags.map(flag => {
                            const isOn = currentFeatures[flag.key] === true
                            return (
                                <Card key={flag.key} className="!p-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <span className="text-2xl">{flag.icon}</span>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-white font-medium text-sm">{flag.label}</span>
                                                    <code className="text-[10px] text-gray-600 bg-white/5 px-1.5 py-0.5 rounded font-mono">{flag.key}</code>
                                                </div>
                                                <p className="text-gray-500 text-xs mt-0.5">{flag.desc}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => toggleFlag(flag.key)}
                                            disabled={saving || !currentLicense}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${isOn
                                                ? 'bg-neon-green/10 text-neon-green border border-neon-green/20 hover:bg-neon-green/20'
                                                : 'bg-white/5 text-gray-500 border border-white/10 hover:bg-white/10 hover:text-white'
                                                }`}
                                        >
                                            {isOn ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                                            {isOn ? 'ON' : 'OFF'}
                                        </button>
                                    </div>
                                </Card>
                            )
                        })}
                    </div>

                    {/* Resumen */}
                    <Card className="mt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <Settings2 size={16} className="text-neon-blue" />
                                <span className="text-gray-400 text-sm">
                                    {Object.values(currentFeatures).filter(Boolean).length} flags activos de {SYSTEM_FLAGS.length}
                                </span>
                            </div>
                            <div className="flex gap-1.5">
                                {SYSTEM_FLAGS.map(f => (
                                    <div
                                        key={f.key}
                                        className={`w-2 h-2 rounded-full transition-colors ${currentFeatures[f.key] ? 'bg-neon-green' : 'bg-gray-700'}`}
                                        title={`${f.label}: ${currentFeatures[f.key] ? 'ON' : 'OFF'}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </Card>
                </>
            )}

            {/* Tab: Historial */}
            {tab === 'history' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2">
                        <History size={16} className="text-neon-blue" /> Cambios de esta sesión
                    </h2>
                    {changeLog.length === 0 ? (
                        <p className="text-gray-600 text-sm text-center py-8">No hay cambios aún. Modifica un flag para ver el historial.</p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                            {changeLog.map((entry, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg text-sm">
                                    <span className="text-gray-600 font-mono text-xs w-16 shrink-0">{entry.time}</span>
                                    <span className="text-gray-400">{entry.license}</span>
                                    <code className="text-neon-blue text-xs bg-neon-blue/5 px-1.5 py-0.5 rounded">{entry.flag}</code>
                                    <span className={`ml-auto font-mono text-xs px-2 py-0.5 rounded ${entry.value ? 'bg-neon-green/10 text-neon-green' : 'bg-red-500/10 text-red-400'}`}>
                                        {entry.value ? 'ON' : 'OFF'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            {/* Tab: JSON Raw */}
            {tab === 'raw' && (
                <Card>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-white font-bold text-sm flex items-center gap-2">
                            <Code2 size={16} className="text-neon-blue" /> JSON de {currentLicense?.client_name || '—'}
                        </h2>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(JSON.stringify(currentFeatures, null, 2))
                                toast.success('JSON copiado')
                            }}
                            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors"
                        >
                            <Copy size={12} /> Copiar
                        </button>
                    </div>
                    <pre className="bg-black/30 rounded-lg p-4 text-xs text-gray-300 font-mono overflow-auto max-h-[400px] custom-scrollbar border border-white/5">
                        {JSON.stringify(currentFeatures, null, 2) || '{}'}
                    </pre>
                </Card>
            )}
        </div>
    )
}
